import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import {
  Building2,
  Calculator,
  Receipt,
  Shield,
  Globe,
  Plane,
  AlertTriangle,
  Landmark,
  Lightbulb,
  Users,
  GitMerge,
  ArrowRight,
  ChevronRight,
  MapPin,
} from "lucide-react";

const ServicesPage = () => {
  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/franjo-vucic-d0pg/30min",
      });
    }
  };

  const serviceCategories = [
    {
      id: "corporate",
      icon: Building2,
      title: "Corporate Services",
      description: "Company formation, corporate structuring and administrative support for companies establishing or operating in Bosnia and Herzegovina.",
      services: ["Company Formation", "Branch Office Setup", "Corporate Administration", "Governance Support"],
    },
    {
      id: "accounting",
      icon: Calculator,
      title: "Accounting Services",
      description: "Financial operations and accounting management for companies operating in Bosnia and Herzegovina.",
      services: ["Bookkeeping", "VAT Compliance", "Payroll Administration", "Financial Reporting"],
    },
    {
      id: "tax",
      icon: Receipt,
      title: "Tax Services",
      description: "Corporate and international tax compliance coordination for companies operating in Bosnia and Herzegovina.",
      services: ["Corporate Tax Advisory", "International Tax", "Transfer Pricing", "VAT Advisory"],
    },
    {
      id: "trusts",
      icon: Shield,
      title: "Trusts, Escrow & Fiduciary",
      description: "Secure transaction structures and fiduciary arrangements for investments in Bosnia and Herzegovina.",
      services: ["Escrow Services", "Security Trustee", "Fiduciary Shareholding", "Asset Protection"],
    },
    {
      id: "citizenship",
      icon: Globe,
      title: "Citizenship & Residence",
      description: "Residence permit support and relocation planning for professionals moving to Bosnia and Herzegovina.",
      services: ["Residence Permits", "Business Residence", "Digital Nomad Relocation", "Naturalisation"],
    },
    {
      id: "relocation",
      icon: Plane,
      title: "Business Relocation",
      description: "Support for companies relocating operations or expanding to Bosnia and Herzegovina.",
      services: ["Personal Relocation", "Corporate Relocation", "Office Setup", "Workforce Setup"],
      highlighted: true,
    },
    {
      id: "compliance",
      icon: AlertTriangle,
      title: "Risk & Compliance",
      description: "Regulatory compliance and governance requirements for businesses in Bosnia and Herzegovina.",
      services: ["Regulatory Compliance", "GDPR Programs", "AML/CFT Compliance", "Risk Management"],
    },
    {
      id: "financial",
      icon: Landmark,
      title: "Regulated Financial Services",
      description: "Support for financial institutions and fintech companies establishing operations in Bosnia and Herzegovina.",
      services: ["Investment Services", "Financial Institution Setup", "Insurance Market Entry", "Fintech Coordination"],
    },
    {
      id: "ip",
      icon: Lightbulb,
      title: "Intellectual Property",
      description: "Protection of intellectual property assets in Bosnia and Herzegovina.",
      services: ["Trademark Registration", "Patent Protection", "Copyright Registration", "IP Portfolio Management"],
    },
    {
      id: "employment",
      icon: Users,
      title: "Employment Advisory",
      description: "Employment structures and workforce management for international companies in Bosnia and Herzegovina.",
      services: ["HR Advisory", "Workforce Structuring", "Employment Law", "Contract Structuring"],
    },
    {
      id: "ma",
      icon: GitMerge,
      title: "Mergers & Acquisitions",
      description: "Support for M&A transactions and restructuring involving companies in Bosnia and Herzegovina.",
      services: ["M&A Coordination", "Due Diligence", "Corporate Restructuring", "Investment Structuring"],
    },
  ];

  return (
    <div data-testid="services-page" className="pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#2F6FED]" />
              <p className="text-sm text-[#2F6FED] uppercase tracking-widest font-semibold">
                Bosnia and Herzegovina
              </p>
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.1] text-[#0B2A4A] mb-6 font-['Sora']">
              Corporate Services in Bosnia and Herzegovina
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              VTZ Backoffice provides corporate and operational infrastructure for 
              international companies entering Bosnia and Herzegovina.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCategories.map((category) => (
              <Link
                key={category.id}
                to={`/services/${category.id}`}
                data-testid={`service-category-${category.id}`}
              >
                <Card
                  className={`h-full bg-white rounded-xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group cursor-pointer ${
                    category.highlighted
                      ? "border-[#2F6FED] shadow-[0_0_20px_-5px_rgba(47,111,237,0.3)]"
                      : "border-slate-100 hover:border-slate-200"
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        category.highlighted ? "bg-[#2F6FED]" : "bg-[#2F6FED]/10"
                      }`}>
                        <category.icon className={`w-6 h-6 ${
                          category.highlighted ? "text-white" : "text-[#2F6FED]"
                        }`} />
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#2F6FED] group-hover:translate-x-1 transition-all" />
                    </div>
                    
                    {category.highlighted && (
                      <span className="inline-block text-xs font-semibold text-[#2F6FED] bg-[#2F6FED]/10 px-2 py-1 rounded mb-3">
                        Popular Service
                      </span>
                    )}
                    
                    <h3 className="text-lg font-semibold text-[#0B2A4A] mb-2 font-['Sora'] group-hover:text-[#2F6FED] transition-colors">
                      {category.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                      {category.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {category.services.slice(0, 3).map((service, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-[#F5F7FA] text-slate-600 px-2 py-1 rounded"
                        >
                          {service}
                        </span>
                      ))}
                      {category.services.length > 3 && (
                        <span className="text-xs text-[#2F6FED] font-medium">
                          +{category.services.length - 3} more
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Doing Business CTA */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-8 md:p-12 shadow-sm border border-slate-100">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-[#0B2A4A] mb-4 font-['Sora']">
                  New to Bosnia and Herzegovina?
                </h2>
                <p className="text-slate-600 mb-6">
                  Read our comprehensive guide to doing business in Bosnia and Herzegovina, 
                  including company formation, tax environment, and compliance requirements.
                </p>
                <Link to="/doing-business-bosnia">
                  <Button className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 text-white font-medium px-6 py-3 rounded-md">
                    Doing Business in BiH Guide
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden lg:flex justify-end">
                <div className="bg-[#F5F7FA] rounded-xl p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#2F6FED]/10 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#2F6FED]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0B2A4A]">Bosnia and Herzegovina</p>
                      <p className="text-sm text-slate-500">Gateway to Western Balkans</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p>• Strategic location in Southeast Europe</p>
                    <p>• Competitive operating costs</p>
                    <p>• Skilled multilingual workforce</p>
                    <p>• Growing business environment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#0B2A4A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6 font-['Sora']">
            Ready to Enter Bosnia and Herzegovina?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Our team can structure a customized service package to meet your specific 
            business requirements in Bosnia and Herzegovina.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="services-cta-book-call"
              onClick={openCalendly}
              className="bg-[#2F6FED] hover:bg-[#2558be] text-white font-medium px-6 py-3 rounded-md transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Schedule a Consultation
            </Button>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2A4A] font-medium px-6 py-3 rounded-md transition-all duration-200"
              >
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
